const N8N_WEBHOOK_URL = 'https://mellow-plan.app.n8n.cloud/webhook/a889d2ae-2159-402f-b326-5f61e90f602e/chat';

export const testN8NConnection = async () => {
  try {
    console.log('Testing N8N connection...');
    const response = await fetch(N8N_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: 'test',
        timestamp: new Date().toISOString(),
        source: 'mellow-frontend',
        sessionId: 'test-session',
      }),
    });
    
    console.log('Test response status:', response.status);
    console.log('Test response ok:', response.ok);
    
    if (response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        console.log('Test JSON response:', data);
      } else {
        const text = await response.text();
        console.log('Test text response:', text);
      }
      return { status: 'connected', message: 'N8N is working correctly' };
    } else if (response.status === 500) {
      console.log('Test failed with status:', response.status);
      return { 
        status: 'fallback', 
        message: 'N8N has issues, using fallback responses. Please check N8N workflow configuration.' 
      };
    } else {
      console.log('Test failed with status:', response.status);
      return { 
        status: 'error', 
        message: `N8N connection failed with status ${response.status}` 
      };
    }
  } catch (error) {
    console.error('Test connection error:', error);
    return { 
      status: 'error', 
      message: 'Network error connecting to N8N' 
    };
  }
};

export const sendMessageToN8N = async (message, sessionId = null) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout for large responses

    console.log('Sending message to N8N:', message);
    console.log('Session ID:', sessionId);

    const response = await fetch(N8N_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        message: message,
        timestamp: new Date().toISOString(),
        source: 'mellow-frontend',
        sessionId: sessionId || Date.now().toString(),
        // Add request for shorter response if needed
        requestType: 'detailed', // or 'brief' for shorter responses
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // If N8N is down, try fallback response
      if (response.status === 500) {
        console.log('N8N returned 500 error, using fallback response');
        return getFallbackResponse(message);
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Handle different response formats
    const contentType = response.headers.get('content-type');
    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers);
    console.log('Content-Type:', contentType);
    
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      console.log('JSON response received, length:', JSON.stringify(data).length);
      
      // Check if response is too large and might cause timeout
      if (data.output && data.output.length > 10000) {
        console.log('Large response detected, length:', data.output.length);
      }
      
      return data;
    } else {
      const text = await response.text();
      console.log('Text response received, length:', text.length);
      return { response: text };
    }
  } catch (error) {
    console.error('Error sending message to N8N:', error);
    
    // Provide more specific error messages
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. The AI response is taking too long. Please try a shorter question or wait a moment and try again.');
    } else if (error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your internet connection and try again.');
    } else if (error.message.includes('HTTP error! status: 500')) {
      console.log('N8N workflow error, using fallback response');
      return getFallbackResponse(message);
    } else {
      throw error;
    }
  }
};

// Fallback response when N8N is down
const getFallbackResponse = (message) => {
  const lowerMessage = message.toLowerCase();
  
  // Simple keyword-based responses
  if (lowerMessage.includes('trip') || lowerMessage.includes('travel') || lowerMessage.includes('vacation')) {
    return {
      output: `I'd be happy to help you plan a trip! While our AI service is being configured, here are some general travel planning tips:

1. **Destination Research**: Start by researching your destination's weather, culture, and attractions
2. **Budget Planning**: Set a realistic budget including flights, accommodation, food, and activities
3. **Booking Timeline**: Book flights 2-3 months in advance, hotels 1-2 months ahead
4. **Travel Documents**: Ensure your passport is valid and check visa requirements
5. **Travel Insurance**: Consider purchasing travel insurance for peace of mind

For more detailed planning assistance, please try again later when our AI service is fully operational.`,
      source: 'fallback',
      timestamp: new Date().toISOString()
    };
  } else if (lowerMessage.includes('japan') || lowerMessage.includes('tokyo') || lowerMessage.includes('kyoto')) {
    return {
      output: `Great choice! Japan is an amazing destination. Here's a quick overview:

**Best Time to Visit**: Spring (cherry blossoms) or Fall (autumn colors)
**Must-See Cities**: Tokyo, Kyoto, Osaka, Hiroshima
**Transportation**: Get a Japan Rail Pass for efficient travel
**Cultural Highlights**: Temples, gardens, hot springs, sushi, ramen
**Language**: Learn basic Japanese phrases for better experience

For detailed itineraries and specific recommendations, please try again when our AI service is available.`,
      source: 'fallback',
      timestamp: new Date().toISOString()
    };
  } else if (lowerMessage.includes('europe') || lowerMessage.includes('paris') || lowerMessage.includes('rome')) {
    return {
      output: `Europe offers incredible diversity! Here are some highlights:

**Popular Routes**: 
- Western Europe: Paris → Amsterdam → Berlin → Prague
- Mediterranean: Rome → Florence → Venice → Barcelona
- Northern Europe: London → Edinburgh → Copenhagen → Stockholm

**Travel Tips**:
- Consider a Eurail pass for multiple countries
- Book popular attractions in advance
- Learn basic phrases in local languages
- Pack for varying weather conditions

For personalized recommendations, please try again when our AI service is working.`,
      source: 'fallback',
      timestamp: new Date().toISOString()
    };
  } else {
    return {
      output: `I'm here to help with travel planning! While our AI service is being configured, here are some general tips:

**Planning Steps**:
1. Choose your destination and travel dates
2. Research local customs and weather
3. Book flights and accommodation early
4. Plan your itinerary with flexibility
5. Pack appropriately for your destination

**Popular Destinations**:
- Asia: Japan, Thailand, Vietnam, South Korea
- Europe: France, Italy, Spain, Germany, Netherlands
- Americas: USA, Canada, Mexico, Brazil, Argentina
- Oceania: Australia, New Zealand, Fiji

For detailed, personalized travel advice, please try again when our AI service is fully operational.`,
      source: 'fallback',
      timestamp: new Date().toISOString()
    };
  }
}; 